import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Col,
  Row,
  Card,
  CardBody,
  Input,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import TableContainer from "../../../components/Common/TableContainer";
import Spinners from "../../../components/Common/Spinner";
import { useGetAllBookings, useCreateInvoice } from "../../../api/booking";
import toastr from "toastr";
import { Badge } from "reactstrap";
import { UncontrolledTooltip } from "reactstrap";

const STATUS_OPTIONS_UI = [
  { value: "all", label: "All Statuses" },
  { value: "pending", label: "Pending" },
  { value: "processing", label: "Processing" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
];

const BOOKING_TYPE_OPTIONS_UI = [
  { value: "all", label: "All Types" },
  { value: "urgent", label: "Urgent" },
  { value: "not_urgent", label: "Not Urgent" },
];

const BookingList = () => {
  document.title = "Bookings List | Admin";

  // server-side controls
  const [status, setStatus] = useState("all");
  const [bookingType, setBookingType] = useState("all");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");

  // mutation hook (GET)
  const { mutate, data, isLoading, isPending, error } = useGetAllBookings();
  const loading = isLoading || isPending;

  // Build a stable fetch key and guard against duplicate calls
  const lastFetchKeyRef = useRef("");
  const fetchKey = JSON.stringify({
    status,
    bookingType,
    search,
  });

  // fire fetch whenever fetchKey changes
  useEffect(() => {
    if (lastFetchKeyRef.current === fetchKey) return;
    lastFetchKeyRef.current = fetchKey;
    mutate({
      status: status === "all" ? undefined : status,
      bookingType: bookingType === "all" ? undefined : bookingType,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchKey]);

  const bookings = data?.data ?? [];
  const total = data?.total ?? 0;

  const [invoiceModal, setInvoiceModal] = useState(false);
  const [selectedBookingForInvoice, setSelectedBookingForInvoice] =
    useState(null);

  const createInvoiceMutation = useCreateInvoice();

  const toggleInvoiceModal = () => {
    setInvoiceModal(!invoiceModal);
    if (invoiceModal) {
      setSelectedBookingForInvoice(null);
    }
  };

  const handleCreateInvoiceClick = (booking) => {
    setSelectedBookingForInvoice(booking);
    setInvoiceModal(true);
  };

  const confirmCreateInvoice = () => {
    if (!selectedBookingForInvoice) return;

    createInvoiceMutation.mutate(selectedBookingForInvoice.slug, {
      onSuccess: () => {
        toastr.success("Invoice created successfully");
        toggleInvoiceModal();
        // Optional: refresh list if needed, or rely on invalidation if correctly set up
        // mutate({...params}) calling fetch won't work well without params here,
        // relying on user manual refresh or optimistic updates is safer for now
        // unless we duplicate the fetch call params state.
      },
      onError: () => {
        // toastr.error("Failed to create invoice"); // Handled by global interceptor usually
      },
    });
  };

  const columns = useMemo(
    () => [
      {
        header: "Booking ID",
        accessorKey: "slug",
        enableColumnFilter: false,
        cell: (info) => {
          const booking = info.row.original;
          return (
            <Link
              to={`/booking-review/${booking.slug}`}
              className="text-primary fw-medium"
            >
              {booking.slug}
            </Link>
          );
        },
      },
      {
        header: "Client",
        accessorKey: "createdBy.fullName",
        enableColumnFilter: false,
        cell: (info) => {
          const booking = info.row.original;
          return booking.createdBy?.fullName || "-";
        },
      },
      {
        header: "Location",
        accessorKey: "location.siteName",
        enableColumnFilter: false,
        cell: (info) => {
          const booking = info.row.original;
          return booking.location?.siteName || "-";
        },
      },
      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
        cell: (info) => {
          const v = String(info.getValue() ?? "").toLowerCase();
          switch (v) {
            case "pending":
              return <Badge className="bg-warning text-dark">Pending</Badge>;
            case "processing":
              return <Badge className="bg-info">Processing</Badge>;
            case "completed":
              return <Badge className="bg-success">Completed</Badge>;
            case "cancelled":
              return <Badge className="bg-danger">Cancelled</Badge>;
            default:
              return (
                <Badge className="bg-secondary">{info.getValue() || "-"}</Badge>
              );
          }
        },
      },
      {
        header: "Booking Type",
        accessorKey: "bookingType",
        enableColumnFilter: false,
        cell: (info) => {
          const v = String(info.getValue() ?? "").toLowerCase();
          switch (v) {
            case "urgent":
              return <Badge className="bg-danger">Urgent</Badge>;
            case "not_urgent":
              return <Badge className="bg-primary">Not Urgent</Badge>;
            default:
              return (
                <Badge className="bg-secondary">{info.getValue() || "-"}</Badge>
              );
          }
        },
      },
      {
        header: "Amount",
        accessorKey: "finalAmount",
        enableColumnFilter: false,
        cell: (info) => {
          const booking = info.row.original;
          const amount =
            booking.finalAmount || booking.actualIntialAmount || "0";
          return `£${parseFloat(amount).toFixed(2)}`;
        },
      },
      {
        header: "PO Required",
        accessorKey: "hasPORequired",
        enableColumnFilter: false,
        cell: (info) => {
          const hasPO = info.getValue();
          return hasPO ? (
            <Badge className="bg-success">Yes</Badge>
          ) : (
            <Badge className="bg-secondary">No</Badge>
          );
        },
      },
      {
        header: "PO Reference",
        accessorKey: "poReference",
        enableColumnFilter: false,
        cell: (info) => info.getValue() || "-",
      },
      {
        header: "Created Date",
        accessorKey: "createdAt",
        enableColumnFilter: false,
        cell: (info) =>
          info.getValue()
            ? new Date(info.getValue()).toLocaleDateString()
            : "-",
      },
      {
        header: "Action",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cellProps) => {
          const booking = cellProps.row.original;
          return (
            <ul className="list-unstyled hstack gap-1 mb-0">
              <li>
                <Link
                  to={`/booking-review/${booking.slug}`}
                  className="btn btn-sm btn-soft-info"
                  id={`viewtooltip-${booking.id}`}
                >
                  View <i className="mdi mdi-eye-outline" />
                  <UncontrolledTooltip
                    placement="top"
                    target={`viewtooltip-${booking.id}`}
                  >
                    View Details
                  </UncontrolledTooltip>
                </Link>
              </li>
              {booking.hasAcceptedByClient &&
                (!booking.invoiceInfo || booking.invoiceInfo.length === 0) && (
                  <li>
                    <Button
                      color="primary"
                      size="sm"
                      className="btn-soft-primary"
                      onClick={() => handleCreateInvoiceClick(booking)}
                      id={`invoiceRequest-${booking.id}`}
                    >
                      <i className="mdi mdi-receipt" />
                      <UncontrolledTooltip
                        placement="top"
                        target={`invoiceRequest-${booking.id}`}
                      >
                        Create Invoice
                      </UncontrolledTooltip>
                    </Button>
                  </li>
                )}
            </ul>
          );
        },
      },
    ],
    [createInvoiceMutation.isPending, invoiceModal] // Add dependencies
  );

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs title="Bookings" breadcrumbItem="Bookings List" />

        <Row className="mb-3 g-2 align-items-center">
          {/* Search */}
          <Col md={4}>
            <Input
              type="text"
              placeholder="Search bookings..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setSearch(searchInput.trim());
                }
              }}
            />
          </Col>

          {/* Status filter */}
          <Col md={2}>
            <Input
              type="select"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
              }}
            >
              {STATUS_OPTIONS_UI.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Input>
          </Col>

          {/* Booking Type filter */}
          <Col md={2}>
            <Input
              type="select"
              value={bookingType}
              onChange={(e) => {
                setBookingType(e.target.value);
              }}
            >
              {BOOKING_TYPE_OPTIONS_UI.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Input>
          </Col>

          <Col md="auto" className="ms-auto d-flex gap-2">
            <Button
              color="primary"
              onClick={() => {
                setSearch(searchInput.trim());
              }}
              disabled={loading}
            >
              <i className="mdi mdi-magnify me-1" />
              Search
            </Button>
            <Button
              color="secondary"
              outline
              onClick={() => {
                setStatus("all");
                setBookingType("all");
                setSearch("");
                setSearchInput("");
              }}
              disabled={loading}
            >
              Reset
            </Button>
            <Button
              color="light"
              onClick={() =>
                mutate({
                  status: status === "all" ? undefined : status,
                  bookingType: bookingType === "all" ? undefined : bookingType,
                })
              }
              disabled={loading}
            >
              <i className="mdi mdi-refresh" />
            </Button>
          </Col>
        </Row>

        <Card>
          <CardBody>
            {loading && bookings.length === 0 ? (
              <Spinners setLoading={() => {}} />
            ) : error ? (
              <div className="text-danger">Failed to load bookings.</div>
            ) : (
              <>
                <TableContainer
                  columns={columns}
                  data={bookings}
                  isPagination={false}
                  isCustomPageSize={false}
                  isGlobalFilter={false}
                  isJobListGlobalFilter={false}
                  tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-check dataTable no-footer dtr-inline mt-2 border-top"
                  theadClass="table-light"
                  divClassName="table-responsive"
                />

                {total > 0 && (
                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <div>
                      Showing {bookings.length} of {total} bookings
                    </div>
                  </div>
                )}
              </>
            )}
          </CardBody>
        </Card>
      </div>
      <Modal isOpen={invoiceModal} toggle={toggleInvoiceModal}>
        <ModalHeader toggle={toggleInvoiceModal}>Create Invoice</ModalHeader>
        <ModalBody>
          Are you sure you want to create an invoice for booking{" "}
          <b>{selectedBookingForInvoice?.slug}</b>?
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={toggleInvoiceModal}
            disabled={createInvoiceMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={confirmCreateInvoice}
            disabled={createInvoiceMutation.isPending}
          >
            {createInvoiceMutation.isPending ? "Creating..." : "Confirm"}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default BookingList;
